import React, { useRef, useState } from "react";
import { Camera, Trash2, Upload } from "lucide-react";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * TicketPhotosPanel — SS-5 (Jun 2026).
 *
 * Admin-only visual evidence gallery for a ticket. Uses the SS-5
 * endpoints (`GET/POST/DELETE /admin/racket-lab/tickets/{id}/photos`)
 * backed by cloud_storage `private_org_documents` (signed URLs ≤5min,
 * soft-delete only). Phases: check_in | in_service | final | pickup.
 */
const PHASES = ["check_in", "in_service", "final", "pickup"];

const PHASE_BADGE = {
  check_in: "bg-blue-100 text-blue-700",
  in_service: "bg-amber-100 text-amber-700",
  final: "bg-emerald-100 text-emerald-700",
  pickup: "bg-violet-100 text-violet-700",
};

export const TicketPhotosPanel = ({ ticketId }) => {
  const t = useRacketLabAdminT();
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("check_in");
  const [caption, setCaption] = useState("");
  const { data, loading, reload } = useAdminApi(
    `/admin/racket-lab/tickets/${encodeURIComponent(ticketId)}/photos`,
    { skip: !ticketId },
  );
  const { run, loading: busy } = useAdminMutation();

  const items = data?.items || [];

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || busy) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("phase", phase);
    if (caption.trim()) fd.append("content_label", caption.trim().slice(0, 64));
    const r = await run(
      "post",
      `/admin/racket-lab/tickets/${encodeURIComponent(ticketId)}/photos`,
      fd,
    ).catch(() => null);
    if (r) {
      if (fileRef.current) fileRef.current.value = "";
      setCaption("");
      reload();
    }
  };

  const removePhoto = async (mediaId) => {
    if (busy) return;
    if (!window.confirm(t("tickets.photos.delete_confirm"))) return;
    const r = await run(
      "delete",
      `/admin/racket-lab/tickets/${encodeURIComponent(ticketId)}/photos/${mediaId}`,
    ).catch(() => null);
    if (r) reload();
  };

  return (
    <section data-testid="rl-ticket-photos-panel" className="rounded-md border border-slate-200 bg-white p-4">
      <h2 className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <Camera className="h-3.5 w-3.5 text-slate-400" />
        {t("tickets.photos.title")}
        <span data-testid="rl-ticket-photos-count" className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-600">
          {items.length}
        </span>
      </h2>
      <p className="mb-3 text-[10px] text-slate-400">{t("tickets.photos.subtitle")}</p>

      {/* Upload form */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 p-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          data-testid="rl-ticket-photos-file-input"
          className="text-[10px] text-slate-600"
        />
        <select
          data-testid="rl-ticket-photos-phase-select"
          value={phase}
          onChange={(e) => setPhase(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1 text-[10px]"
        >
          {PHASES.map((p) => (
            <option key={p} value={p}>{t(`tickets.photos.phase_${p}`)}</option>
          ))}
        </select>
        <input
          type="text"
          data-testid="rl-ticket-photos-caption-input"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={t("tickets.photos.caption_placeholder")}
          maxLength={64}
          className="rounded-md border border-slate-300 px-2 py-1 text-[10px]"
        />
        <button
          type="button"
          data-testid="rl-ticket-photos-upload-btn"
          onClick={upload}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[10px] font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Upload className="h-3 w-3" />
          {busy ? t("tickets.photos.uploading") : t("tickets.photos.upload_btn")}
        </button>
        <span className="text-[9px] text-slate-400">{t("tickets.photos.upload_hint")}</span>
      </div>

      {/* Gallery */}
      {loading ? (
        <p className="text-xs text-slate-500">{t("tickets.photos.loading")}</p>
      ) : items.length === 0 ? (
        <p data-testid="rl-ticket-photos-empty" className="text-xs text-slate-500">
          {t("tickets.photos.empty")}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((ph) => (
            <figure
              key={ph.media_id}
              data-testid={`rl-ticket-photo-${ph.media_id}`}
              className="group relative overflow-hidden rounded-md border border-slate-200"
            >
              <a href={ph.signed_url} target="_blank" rel="noreferrer">
                <img
                  src={ph.signed_url}
                  alt={ph.content_label || ph.phase || "ticket photo"}
                  className="h-28 w-full object-cover"
                  loading="lazy"
                />
              </a>
              <figcaption className="flex items-center justify-between gap-1 px-1.5 py-1">
                <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-medium ${PHASE_BADGE[ph.phase] || "bg-slate-100 text-slate-600"}`}>
                  {t(`tickets.photos.phase_${ph.phase}`)}
                </span>
                <button
                  type="button"
                  data-testid={`rl-ticket-photo-delete-${ph.media_id}`}
                  onClick={() => removePhoto(ph.media_id)}
                  title={t("tickets.photos.delete_btn")}
                  className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </figcaption>
              {ph.content_label ? (
                <p className="truncate px-1.5 pb-1 text-[9px] text-slate-500">{ph.content_label}</p>
              ) : null}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
};

export default TicketPhotosPanel;
